import { useEffect, useState } from 'react'
import { getPlatformStats, type PlatformStats, type PlatformTenantRow } from '@/api/platformApi'
import {
  buildPlatformReportData,
  downloadPlatformReportCsv,
  openPlatformReportDocument,
  registerPlatformReportDownloadHandler,
} from '@/utils/platformReport'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Platform.module.css'

type Props = {
  tenants: PlatformTenantRow[]
  stats: PlatformStats | null
  disabled?: boolean
}

export function PlatformExportActions({ tenants, stats, disabled }: Props) {
  const [busy, setBusy] = useState<'csv' | 'doc' | null>(null)
  const [erro, setErro] = useState('')
  const { notifySaved, notifyError, notifyInfo } = useSaveFeedback()

  useEffect(() => {
    registerPlatformReportDownloadHandler()
  }, [])

  const resolveReport = async () => {
    let resolvedStats = stats
    if (!resolvedStats) {
      resolvedStats = await getPlatformStats()
    }
    return buildPlatformReportData(tenants, resolvedStats)
  }

  const handleCsv = async () => {
    setErro('')
    setBusy('csv')
    try {
      const data = await resolveReport()
      downloadPlatformReportCsv(data)
      notifySaved('CSV exportado com sucesso.')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao exportar CSV.'
      setErro(msg)
      notifyError(e, msg)
    } finally {
      setBusy(null)
    }
  }

  const handleDocument = async () => {
    setErro('')
    setBusy('doc')
    try {
      const data = await resolveReport()
      openPlatformReportDocument(data)
      notifyInfo('Relatório aberto. Use Imprimir no browser para guardar em PDF.')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao gerar documento.'
      const display = msg.includes('descarregado') ? msg : msg
      setErro(display)
      if (msg.includes('descarregado')) notifyInfo(display)
      else notifyError(e, display)
    } finally {
      setBusy(null)
    }
  }

  const isBusy = busy !== null

  return (
    <div className={styles.exportActions}>
      <button
        type="button"
        className={styles.btnOutline}
        onClick={() => handleCsv()}
        disabled={disabled || isBusy || tenants.length === 0}
        title="Ficheiro CSV para Excel ou Google Sheets"
      >
        {busy === 'csv' ? 'A exportar…' : 'Exportar CSV'}
      </button>
      <button
        type="button"
        className={styles.btnOutline}
        onClick={() => handleDocument()}
        disabled={disabled || isBusy || tenants.length === 0}
        title="Pré-visualização com impressão e download de PDF"
      >
        {busy === 'doc' ? 'A gerar…' : 'Relatório PDF'}
      </button>
      {erro && (
        <p className={styles.exportErro} role="alert">
          {erro}
        </p>
      )}
    </div>
  )
}
